import {
    createFeatureFactoryConfig,
    getFeatureFlag,
} from 'utils/configHelpers';

import proj4 from 'proj4';
import { register } from 'ol/proj/proj4.js';
import * as proj from 'ol/proj';

// EPSG:8059 - GDA2020 / SA Lambert. This is the canonical CRS for SA
// risk-planning spatial data: the geoserver/asset PostGIS columns are
// `geometry(..., 8059)`, the GeoServer FocusArea / FuelManagementRegister /
// FaftLine layers declare `srs EPSG:8059` (FORCE_DECLARED), and the API's
// DATA_PROJECTION is 8059. The frontend previously used EPSG:28354 (MGA
// Zone 54), so drawn geometry was sent as MGA-Zone-54 numbers, which the API
// stored verbatim under SRID 8059 — GeoServer then reprojected those bad
// coordinates out into the Banda Sea (~127°E, +4°N), north of Australia.
proj4.defs(
    'EPSG:8059',
    '+proj=lcc +lat_0=-32 +lon_0=135 +lat_1=-28 +lat_2=-36 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
);
register(proj4);

/**
 * 'dev': Is intended to be when the app is run locally using `yarn start`
 * 'uat': Is intended to be a non-production deploy of the application
 * 'prod': Is the prod deploy of the application
 */
type ENVVALUES = 'dev' | 'uat' | 'prod';

/**
 * The environment the application is currently in.
 * Reminder: This value is set at build-time
 */
export const ENV: ENVVALUES =
    (import.meta.env.REACT_APP_ENV?.toLowerCase() as ENVVALUES) ?? 'dev';

export const CLIENT_ID = (() => {
    if (import.meta.env.REACT_APP_CLIENT_ID)
        return import.meta.env.REACT_APP_CLIENT_ID;

    // if (ENV === 'uat') return '';

    return '4auij1qb3rot2b0ae7bqlck822';
})();

export const API_URL = (() => {
    if (import.meta.env.REACT_APP_API_URL)
        return import.meta.env.REACT_APP_API_URL;

    // if (ENV === 'uat') return '';

    return 'https://bip-pilot-dev.sbcc.sa.gov.au/api/v1';
})();

export const GEOSERVER_URL = (() => {
    if (import.meta.env.REACT_APP_GEOSERVER_URL)
        return import.meta.env.REACT_APP_GEOSERVER_URL;

    // if (ENV === 'uat') return '';

    return 'https://bip-pilot-dev.sbcc.sa.gov.au/geoserver';
})();

export const ARCGIS_PROXY_URL = (() => {
    const geoserverUrl = GEOSERVER_URL;
    return geoserverUrl.replace(/\/geoserver/i, '/arcgis');
})();

export const COGNITO_URL = (() => {
    if (import.meta.env.REACT_APP_COGNITO_URL)
        return import.meta.env.REACT_APP_COGNITO_URL;

    // if (ENV === 'uat') return '';

    return 'https://dev-bfrp.auth.ap-southeast-2.amazoncognito.com';
})();

export const BANNER_COLOR = (() => {
    if (ENV === 'dev') return '#FFFFE5';
    return '#F5F5F5';
})();

export const LOCALSTORAGE = {
    AUTH: 'auth',
    BOOKMARKS: 'bookmarks',
    THEMEMODE: 'theme-mode',
    SELECTEDBFMC: 'selected-bfmc',
    SELECTEDPLANTYPE: 'selected-plan-type',
};

export const SA_BOUNDS = {
    minLong: 129.0,
    minLat: -38.1,
    maxLong: 141.0,
    maxLat: -26.0,
};

export const SEARCH_CONFIG = {
    // SA CFS address search API — to be configured when available
    EPLANNING_API_URL: '',
    PLACE_HOLDER_TEXT_ADDRESS: 'Search by Address',
    PLACE_HOLDER_TEXT_LATLONG: 'Select by Lat / Long',
    EXAMPLE_TEXT_ADDRESS: 'eg: 60 Waymouth St, Adelaide SA 5000',
    EXAMPLE_TEXT_LATLONG: 'eg: -34.928889, 138.600833',
    PROTOCOL_HTTP: 'http:',
    PROTOCOL_HTTPS: 'https:',
};

/**
 * The time in milliseconds between attempting to refresh the auth token using a refresh token
 */
export const REFRESH_DELAY = 18 * 60_000; // 18 minutes

export const FAFT_PRIORITISATION_TASK_STATUS = {
    PENDING: 'Pending',
    STARTED: 'Started',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    FAILED: 'Failed',
};

export const OP_FIRE_TRAIL = {
    layerId: 'FireTrails',
    attribute: 'FireTrailId',
    serviceType: 'ImageArcGISRestLayer',
    // TODO: Update service URL when CFS ArcGIS service path is confirmed
    serviceUrl:
        '${ARCGIS_PROXY_URL}/rest/services/CFS_Custodial_Read/MapServer',
    serviceId: 2,
};

export enum FEATURE {
    FAFT = 'FAFT',
    FAFT_UI = 'FAFT_UI',
}

export enum FEATURE_ACTION_TYPE {
    GEOMETRY_EDIT = 'geometry-edit',
    ATTRIBUTE_EDIT = 'attribute-edit',
}

export const isFeatureEnabled = createFeatureFactoryConfig({
    [FEATURE.FAFT]: getFeatureFlag(
        import.meta.env.REACT_APP_ENABLE_FEATURE,
        FEATURE.FAFT,
    ),
    [FEATURE.FAFT_UI]: getFeatureFlag(
        import.meta.env.REACT_APP_ENABLE_FEATURE,
        FEATURE.FAFT_UI,
    ),
});

export const MAP_PROJECTION = proj.get('EPSG:3857')!;
// GDA2020 / SA Lambert — must match the API's DATA_PROJECTION and the
// GeoServer layer SRS / PostGIS column SRID (all EPSG:8059).
export const DATA_PROJECTION = proj.get('EPSG:8059')!;
